<template>
  <div class="bulk-episode-workflow">
    <!-- Storage Browser Section -->
    <div class="box storage-browser-box">
      <div class="level">
        <div class="level-left">
          <h4 class="subtitle is-5">
            <span class="icon"><i class="mdi mdi-folder-open"></i></span>
            Browse Input Storage
          </h4>
        </div>
        <div class="level-right">
          <b-field grouped>
            <b-select v-model="selectedStorageConfigId" placeholder="Select Storage" :loading="loadingConfigs">
              <option v-for="config in storageConfigs" :key="config.id" :value="config.id">
                {{ config.config_name }} ({{ config.bucket_name || config.base_folder || 'default' }})
              </option>
            </b-select>
            <b-button
              type="is-info"
              icon-left="refresh"
              :loading="loadingBrowser"
              :disabled="!selectedStorageConfigId"
              @click="browseStorage('')"
            >
              Refresh
            </b-button>
          </b-field>
        </div>
      </div>

      <!-- Breadcrumb Navigation -->
      <nav v-if="currentPath" class="breadcrumb has-bullet-separator mb-2" aria-label="breadcrumbs">
        <ul>
          <li>
            <a href="#" @click.prevent="browseStorage('')">
              <span class="icon"><i class="mdi mdi-home"></i></span>
              Root
            </a>
          </li>
          <li v-for="(part, index) in pathParts" :key="index">
            <a href="#" @click.prevent="browseStorage(pathParts.slice(0, index + 1).join('/'))">
              {{ part }}
            </a>
          </li>
        </ul>
      </nav>

      <!-- Search within folder -->
      <b-field grouped class="mb-2">
        <b-input
          v-model="browserSearchTerm"
          placeholder="Filter files in current folder..."
          icon="magnify"
          expanded
          @keyup.native.enter="filterBrowserFiles"
        />
        <b-button icon-left="filter" @click="filterBrowserFiles">Filter</b-button>
        <b-button icon-left="close" type="is-light" @click="clearBrowserSearch">Clear</b-button>
      </b-field>

      <!-- Folder & File List -->
      <div class="browser-content" style="max-height: 300px; overflow-y: auto; border: 1px solid #dbdbdb; border-radius: 4px;">
        <div v-if="loadingBrowser" class="has-text-centered py-4">
          <b-loading :is-full-page="false" :active="true" />
        </div>
        <table v-else class="table is-fullwidth is-hoverable is-narrow mb-0">
          <thead>
            <tr>
              <th width="30"></th>
              <th>Name</th>
              <th width="100">Size</th>
              <th width="120">Action</th>
            </tr>
          </thead>
          <tbody>
            <!-- Folders -->
            <tr v-for="folder in filteredFolders" :key="'folder-' + folder.name" class="folder-row" @click="browseStorage(folder.path)">
              <td><span class="icon has-text-warning"><i class="mdi mdi-folder"></i></span></td>
              <td><strong>{{ folder.name }}</strong></td>
              <td>-</td>
              <td>
                <b-button size="is-small" icon-left="folder-open" @click.stop="browseStorage(folder.path)">Open</b-button>
              </td>
            </tr>
            <!-- Files -->
            <tr v-for="file in filteredFiles" :key="'file-' + file.name" class="file-row">
              <td><span class="icon has-text-info"><i class="mdi mdi-file-video"></i></span></td>
              <td>
                <span class="is-size-7">{{ file.name }}</span>
              </td>
              <td><span class="is-size-7 has-text-grey">{{ formatFileSize(file.size) }}</span></td>
              <td>
                <b-button
                  size="is-small"
                  type="is-primary"
                  icon-left="link"
                  @click="selectFileForLinking(file)"
                >
                  Link
                </b-button>
              </td>
            </tr>
            <!-- Empty state -->
            <tr v-if="filteredFolders.length === 0 && filteredFiles.length === 0 && !loadingBrowser">
              <td colspan="4" class="has-text-centered has-text-grey py-4">
                <span class="icon"><i class="mdi mdi-folder-alert"></i></span>
                No files or folders found
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Load More -->
      <div v-if="browserContinuationToken" class="has-text-centered mt-2">
        <b-button size="is-small" :loading="loadingMore" @click="loadMoreFiles">
          Load More Files...
        </b-button>
      </div>
    </div>

    <!-- Auto-Suggest Section -->
    <div class="box">
      <div class="level">
        <div class="level-left">
          <h4 class="subtitle is-5">
            <span class="icon"><i class="mdi mdi-folder-search"></i></span>
            Auto-Suggest TX Files
          </h4>
        </div>
        <div class="level-right">
          <b-field grouped>
            <b-select v-model="selectedStorageConfigId" placeholder="Select Storage Config" :loading="loadingConfigs">
              <option v-for="config in storageConfigs" :key="config.id" :value="config.id">
                {{ config.config_name }} ({{ config.bucket_name || config.base_folder || 'No path' }})
              </option>
            </b-select>
            <b-tooltip label="Learn pattern from an example filename" position="is-bottom">
              <b-button
                type="is-link"
                icon-left="school"
                outlined
                @click="openLearnPatternModal"
              >
                Learn Pattern
              </b-button>
            </b-tooltip>
            <b-button
              type="is-primary"
              icon-left="magnify"
              :loading="suggesting"
              :disabled="!selectedStorageConfigId"
              @click="runAutoSuggest"
            >
              Auto-Suggest All
            </b-button>
          </b-field>
        </div>
      </div>

      <!-- Learned Pattern Display -->
      <div v-if="learnedPattern || learnedPath" class="notification is-light is-link mt-2 py-2">
        <div class="level is-mobile">
          <div class="level-left">
            <span class="icon"><i class="mdi mdi-school"></i></span>
            <span v-if="learnedPattern" class="ml-2"><strong>Pattern:</strong> <code>{{ learnedPattern }}</code></span>
            <b-tag v-if="learnedPattern" type="is-info" class="ml-2">{{ Math.round(learnedPatternConfidence * 100) }}%</b-tag>
            <span v-if="learnedPath" class="ml-2"><strong>Path:</strong> <code>{{ learnedPath }}</code></span>
          </div>
          <div class="level-right">
            <b-button type="is-text" size="is-small" icon-left="close" @click="clearLearnedPattern">
              Clear
            </b-button>
          </div>
        </div>
      </div>

      <!-- Suggestion Stats -->
      <div v-if="suggestionResults" class="suggestion-results mt-4">
        <div class="level">
          <div class="level-item has-text-centered">
            <div>
              <p class="heading">Total Episodes</p>
              <p class="title">{{ suggestionResults.stats.totalEpisodes }}</p>
            </div>
          </div>
          <div class="level-item has-text-centered">
            <div>
              <p class="heading">Files Found</p>
              <p class="title has-text-success">{{ suggestionResults.stats.matched }}</p>
            </div>
          </div>
          <div class="level-item has-text-centered">
            <div>
              <p class="heading">Not Found</p>
              <p class="title has-text-warning">{{ suggestionResults.stats.notFound }}</p>
            </div>
          </div>
          <div class="level-item has-text-centered">
            <div>
              <p class="heading">Already Linked</p>
              <p class="title has-text-info">{{ suggestionResults.stats.alreadyLinked }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Episode Status Table -->
    <div class="box">
      <div class="level">
        <div class="level-left">
          <h4 class="subtitle is-5">
            <span class="icon"><i class="mdi mdi-format-list-bulleted"></i></span>
            Episode TX File Status
          </h4>
        </div>
        <div class="level-right">
          <b-field grouped>
            <b-checkbox v-model="selectAllEpisodes" @input="toggleSelectAll">
              Select All
            </b-checkbox>
            <b-tag type="is-info">{{ selectedEpisodeIds.length }} selected</b-tag>
            <b-button
              v-if="foundSuggestionCount > 0"
              type="is-success"
              size="is-small"
              icon-left="check-all"
              :loading="linkingAllFound"
              @click="linkAllFoundSuggestions"
            >
              Link {{ foundSuggestionCount }} Found
            </b-button>
            <b-button
              v-if="selectedLinkedCount > 0"
              type="is-warning"
              size="is-small"
              icon-left="link-off"
              :loading="unlinkingAll"
              @click="unlinkSelectedFiles"
            >
              Unlink {{ selectedLinkedCount }}
            </b-button>
          </b-field>
        </div>
      </div>

      <b-table
        v-if="episodes && episodes.length > 0"
        :data="episodes"
        :hoverable="true"
        :striped="true"
        :narrowed="true"
        :paginated="episodes.length > 25"
        :per-page="25"
        :checked-rows.sync="selectedRows"
        checkable
      >
        <template #default="props">
          <b-table-column field="episode_number" label="Ep #" width="60" sortable numeric>
            {{ props.row.episode_number }}
          </b-table-column>
          <b-table-column field="title" label="Episode Title" width="180">
            {{ props.row.title || `Episode ${props.row.episode_number}` }}
          </b-table-column>
          <b-table-column field="txStatus" label="Status" width="90">
            <b-tag v-if="props.row.txLinked" type="is-success" size="is-small">Linked</b-tag>
            <b-tag v-else-if="props.row.suggestion" type="is-info" size="is-small">Found</b-tag>
            <b-tag v-else type="is-light" size="is-small">Not Linked</b-tag>
          </b-table-column>
          <b-table-column field="sourceFile" label="Source File (Input)">
            <div v-if="props.row.sourcePath" class="is-size-7">
              <span class="icon has-text-success is-small"><i class="mdi mdi-check-circle"></i></span>
              <code class="source-path" :title="props.row.sourcePath">{{ truncatePath(props.row.sourcePath) }}</code>
            </div>
            <div v-else-if="props.row.suggestion" class="is-size-7 has-text-info">
              <span class="icon is-small"><i class="mdi mdi-file-find"></i></span>
              <code class="source-path" :title="props.row.suggestion.path">{{ truncatePath(props.row.suggestion.path) }}</code>
              <span class="ml-2">
                <b-button
                  size="is-small"
                  type="is-success"
                  icon-left="check"
                  :loading="props.row.linkingFound"
                  @click="linkFoundSuggestion(props.row)"
                >
                  Link
                </b-button>
                <b-button
                  size="is-small"
                  type="is-light"
                  icon-left="close"
                  class="ml-1"
                  @click="dismissSuggestion(props.row)"
                >
                  Dismiss
                </b-button>
              </span>
            </div>
            <div v-else class="is-size-7">
              <b-button
                size="is-small"
                type="is-light"
                icon-left="link-plus"
                @click="openLinkModal(props.row)"
              >
                Browse & Link
              </b-button>
            </div>
          </b-table-column>
          <b-table-column field="txFilename" label="TX Filename (Output)" width="220">
            <span v-if="props.row.txFilename" class="is-size-7">
              <code>{{ props.row.txFilename }}</code>
            </span>
            <span v-else class="has-text-grey is-size-7 is-italic">Will be generated</span>
          </b-table-column>
          <b-table-column field="qcStatus" label="QC" width="70">
            <b-tag v-if="props.row.qcStatus === 'passed'" type="is-success" size="is-small">Pass</b-tag>
            <b-tag v-else-if="props.row.qcStatus === 'failed'" type="is-danger" size="is-small">Fail</b-tag>
            <b-tag v-else-if="props.row.qcStatus === 'processing'" type="is-info" size="is-small">...</b-tag>
            <span v-else class="has-text-grey">-</span>
          </b-table-column>
          <b-table-column field="playoutReady" label="Playout" width="70">
            <b-icon v-if="props.row.playoutReady" icon="check-circle" type="is-success" size="is-small" />
            <span v-else class="has-text-grey">-</span>
          </b-table-column>
          <b-table-column label="" width="50">
            <b-dropdown aria-role="list" position="is-bottom-left">
              <template #trigger>
                <b-button size="is-small" icon-left="dots-vertical" type="is-light" />
              </template>
              <b-dropdown-item v-if="props.row.suggestion && !props.row.txLinked" aria-role="listitem" @click="linkFoundSuggestion(props.row)">
                <span class="icon has-text-success"><i class="mdi mdi-check"></i></span> Accept Suggestion
              </b-dropdown-item>
              <b-dropdown-item v-if="props.row.suggestion && !props.row.txLinked" aria-role="listitem" @click="dismissSuggestion(props.row)">
                <span class="icon has-text-grey"><i class="mdi mdi-close"></i></span> Dismiss Suggestion
              </b-dropdown-item>
              <b-dropdown-item aria-role="listitem" @click="openLinkModal(props.row)">
                <span class="icon"><i class="mdi mdi-link"></i></span> Browse & Link
              </b-dropdown-item>
              <b-dropdown-item v-if="props.row.sourcePath" aria-role="listitem" @click="unlinkFile(props.row)">
                <span class="icon has-text-danger"><i class="mdi mdi-link-off"></i></span> Unlink
              </b-dropdown-item>
            </b-dropdown>
          </b-table-column>
        </template>
      </b-table>

      <div v-else class="has-text-centered has-text-grey py-4">
        <b-icon icon="information" />
        <p>Loading episodes...</p>
      </div>
    </div>

    <!-- Workflow Selection & Actions -->
    <div v-if="selectedEpisodeIds.length > 0" class="box">
      <h4 class="subtitle is-5">
        <span class="icon"><i class="mdi mdi-cog-transfer"></i></span>
        Workflow Actions ({{ selectedEpisodeIds.length }} episodes selected)
      </h4>

      <div class="columns">
        <div class="column is-3">
          <b-field label="Workflow Type">
            <b-select v-model="selectedWorkflowType" expanded>
              <option value="qc">QC Analysis</option>
              <option value="transcode">Transcode</option>
              <option value="tx_ingest">TX Ingest (Link Files)</option>
              <option value="playout">Playout Delivery</option>
              <option value="vod">VOD Delivery</option>
            </b-select>
          </b-field>
        </div>
        <div class="column is-3">
          <b-field :label="allSelectedLinked ? 'Input Storage (Linked)' : 'Input Storage (Source)'">
            <b-select
              v-model="selectedStorageConfigId"
              :placeholder="allSelectedLinked ? 'From linked files' : 'Select input'"
              expanded
              :loading="loadingConfigs"
              :disabled="allSelectedLinked"
            >
              <option v-for="config in storageConfigs" :key="config.id" :value="config.id">
                {{ config.config_name }}
              </option>
            </b-select>
          </b-field>
          <p v-if="allSelectedLinked" class="help is-success">
            <b-icon icon="check-circle" size="is-small" /> {{ linkedCount }} files already linked
          </p>
        </div>
        <div class="column is-3">
          <b-field label="Processing Workflow">
            <b-select v-model="selectedWorkflowId" placeholder="Select workflow" expanded>
              <option v-for="workflow in filteredWorkflows" :key="workflow.id" :value="workflow.id">
                {{ workflow.template_name }}
              </option>
            </b-select>
          </b-field>
        </div>
        <div class="column is-3">
          <b-field label="Output Storage">
            <b-select v-model="selectedOutputStorageId" placeholder="Select output" expanded>
              <option v-for="config in outputStorageConfigs" :key="config.id" :value="config.id">
                {{ config.config_name }}
              </option>
            </b-select>
          </b-field>
        </div>
      </div>

      <div class="buttons is-centered mt-4">
        <!-- Link TX Files (when suggestions available) -->
        <b-button
          v-if="episodesWithSuggestions > 0"
          type="is-success"
          icon-left="link"
          :loading="linkingFiles"
          @click="linkSuggestedFiles"
        >
          Link {{ episodesWithSuggestions }} Found Files
        </b-button>

        <!-- Submit Workflow Jobs -->
        <b-button
          type="is-primary"
          size="is-medium"
          icon-left="play"
          :loading="submittingJobs"
          :disabled="!canSubmitJobs"
          @click="submitWorkflowJobs"
        >
          Submit {{ workflowLabel }} Jobs
        </b-button>

        <!-- Approve for Playout (after QC passes) -->
        <b-button
          v-if="qcPassedCount > 0"
          type="is-warning"
          icon-left="check-decagram"
          :loading="approvingPlayout"
          @click="approveForPlayout"
        >
          Approve {{ qcPassedCount }} for Playout
        </b-button>
      </div>
    </div>

    <!-- Recent Jobs -->
    <div v-if="recentJobs.length > 0" class="box">
      <h5 class="subtitle is-6">
        <span class="icon"><i class="mdi mdi-history"></i></span>
        Recent Processing Jobs
      </h5>
      <b-table
        :data="recentJobs"
        :hoverable="true"
        :striped="true"
        :narrowed="true"
        :paginated="recentJobs.length > 10"
        :per-page="10"
      >
        <template #default="props">
          <b-table-column field="episodeNumber" label="Ep #" width="60">
            {{ props.row.episodeNumber }}
          </b-table-column>
          <b-table-column field="type" label="Type" width="100">
            {{ props.row.type }}
          </b-table-column>
          <b-table-column field="status" label="Status" width="100">
            <b-tag :type="getStatusType(props.row.status)" size="is-small">
              {{ props.row.status }}
            </b-tag>
          </b-table-column>
          <b-table-column field="createdAt" label="Time" width="120">
            {{ formatTimeAgo(props.row.createdAt) }}
          </b-table-column>
        </template>
      </b-table>
    </div>

    <!-- Learn Pattern Modal -->
    <b-modal :active.sync="showLearnPatternModal" has-modal-card trap-focus>
      <template #default>
        <div class="modal-card" style="width: 600px;">
          <header class="modal-card-head">
            <p class="modal-card-title">
              <span class="icon"><i class="mdi mdi-school"></i></span>
              Learn Pattern from Example
            </p>
            <button type="button" class="delete" @click="showLearnPatternModal = false" />
          </header>
          <section class="modal-card-body">
            <div class="notification is-info is-light">
              <p>Paste an example TX filename. The system will learn your naming pattern.</p>
            </div>

            <b-field label="Example Filename *">
              <b-input
                v-model="exampleFilename"
                placeholder="e.g., TX_MOJALOVE_SHOW_S01E001_1080i.mxf"
                expanded
              />
            </b-field>

            <b-field label="Episode Number (for this example)">
              <b-numberinput
                v-model="exampleEpisodeNumber"
                :min="1"
                :max="9999"
                controls-position="compact"
              />
            </b-field>

            <b-field label="Search Folder Path (optional)" message="Limit auto-suggest to files in this subfolder (relative to storage config base folder)">
              <b-input
                v-model="learnedPath"
                placeholder="e.g., ReaTsotella/Season25 or leave empty to search all folders"
                expanded
              />
            </b-field>

            <div v-if="learnedPattern" class="notification is-success is-light mt-4">
              <p class="has-text-weight-bold">Learned Pattern:</p>
              <code class="is-size-5">{{ learnedPattern }}</code>
              <p class="mt-2 is-size-7">Confidence: {{ Math.round(learnedPatternConfidence * 100) }}%</p>
            </div>
          </section>
          <footer class="modal-card-foot">
            <b-button @click="showLearnPatternModal = false">Cancel</b-button>
            <b-button type="is-primary" :loading="learningPattern" @click="learnPattern">
              Learn Pattern
            </b-button>
            <b-button v-if="learnedPattern" type="is-success" @click="applyLearnedPattern">
              Apply & Close
            </b-button>
          </footer>
        </div>
      </template>
    </b-modal>

    <!-- Link File Modal -->
    <b-modal :active.sync="showLinkModal" has-modal-card trap-focus width="900">
      <template #default>
        <div class="modal-card" style="width: 900px;">
          <header class="modal-card-head">
            <p class="modal-card-title">
              <span class="icon"><i class="mdi mdi-link"></i></span>
              Link Source File to Episode {{ linkingEpisode ? linkingEpisode.episode_number : '' }}
            </p>
            <button type="button" class="delete" @click="showLinkModal = false" />
          </header>
          <section class="modal-card-body">
            <!-- Episode Info -->
            <div v-if="linkingEpisode" class="notification is-light mb-4">
              <div class="columns">
                <div class="column is-6">
                  <p><strong>Episode:</strong> {{ linkingEpisode.title || `Episode ${linkingEpisode.episode_number}` }}</p>
                  <p><strong>Expected TX Filename:</strong> <code>{{ linkingEpisode.txFilename || 'Will be generated' }}</code></p>
                </div>
                <div class="column is-6">
                  <p><strong>Current Source:</strong>
                    <span v-if="linkingEpisode.sourcePath" class="has-text-success">{{ truncatePath(linkingEpisode.sourcePath) }}</span>
                    <span v-else class="has-text-grey">Not linked</span>
                  </p>
                </div>
              </div>
            </div>

            <!-- Selected File Preview -->
            <div v-if="selectedFileForLink" class="notification is-success is-light mb-4">
              <div class="level">
                <div class="level-left">
                  <div>
                    <p class="has-text-weight-bold">
                      <span class="icon"><i class="mdi mdi-check-circle"></i></span>
                      Selected: {{ selectedFileForLink.name }}
                    </p>
                    <p class="is-size-7">Path: {{ selectedFileForLink.path }}</p>
                    <p class="is-size-7">Size: {{ formatFileSize(selectedFileForLink.size) }}</p>
                  </div>
                </div>
                <div class="level-right">
                  <b-button type="is-light" size="is-small" icon-left="close" @click="selectedFileForLink = null">
                    Clear Selection
                  </b-button>
                </div>
              </div>
            </div>

            <!-- Storage Browser in Modal -->
            <b-field label="Storage" grouped>
              <b-select v-model="modalStorageConfigId" expanded>
                <option v-for="config in storageConfigs" :key="config.id" :value="config.id">
                  {{ config.config_name }} ({{ config.bucket_name || config.base_folder || 'default' }})
                </option>
              </b-select>
              <b-button icon-left="refresh" @click="browseModalStorage('')">Refresh</b-button>
            </b-field>

            <!-- Modal Breadcrumb -->
            <nav v-if="modalCurrentPath" class="breadcrumb has-bullet-separator mb-2" aria-label="breadcrumbs">
              <ul>
                <li>
                  <a href="#" @click.prevent="browseModalStorage('')">
                    <span class="icon"><i class="mdi mdi-home"></i></span> Root
                  </a>
                </li>
                <li v-for="(part, index) in modalPathParts" :key="index">
                  <a href="#" @click.prevent="browseModalStorage(modalPathParts.slice(0, index + 1).join('/'))">
                    {{ part }}
                  </a>
                </li>
              </ul>
            </nav>

            <!-- Modal File Browser -->
            <div class="modal-browser-content" style="max-height: 350px; overflow-y: auto; border: 1px solid #dbdbdb; border-radius: 4px;">
              <div v-if="loadingModalBrowser" class="has-text-centered py-4">
                <b-loading :is-full-page="false" :active="true" />
              </div>
              <table v-else class="table is-fullwidth is-hoverable is-narrow mb-0">
                <thead>
                  <tr>
                    <th width="30"></th>
                    <th>Name</th>
                    <th width="100">Size</th>
                    <th width="80">Select</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="folder in modalFolders" :key="'mf-' + folder.name" class="folder-row" @click="browseModalStorage(folder.path)">
                    <td><span class="icon has-text-warning"><i class="mdi mdi-folder"></i></span></td>
                    <td><strong>{{ folder.name }}</strong></td>
                    <td>-</td>
                    <td>
                      <b-button size="is-small" icon-left="folder-open" @click.stop="browseModalStorage(folder.path)">Open</b-button>
                    </td>
                  </tr>
                  <tr
                    v-for="file in modalFiles"
                    :key="'mfile-' + file.name"
                    class="file-row"
                    :class="{ 'is-selected': selectedFileForLink && selectedFileForLink.path === file.path }"
                    @click="selectFileForLink(file)"
                  >
                    <td><span class="icon has-text-info"><i class="mdi mdi-file-video"></i></span></td>
                    <td><span class="is-size-7">{{ file.name }}</span></td>
                    <td><span class="is-size-7 has-text-grey">{{ formatFileSize(file.size) }}</span></td>
                    <td>
                      <b-button
                        size="is-small"
                        :type="selectedFileForLink && selectedFileForLink.path === file.path ? 'is-success' : 'is-light'"
                        :icon-left="selectedFileForLink && selectedFileForLink.path === file.path ? 'check' : 'radiobox-blank'"
                        @click.stop="selectFileForLink(file)"
                      />
                    </td>
                  </tr>
                  <tr v-if="modalFolders.length === 0 && modalFiles.length === 0 && !loadingModalBrowser">
                    <td colspan="4" class="has-text-centered has-text-grey py-4">
                      No files or folders found
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
          <footer class="modal-card-foot">
            <b-button @click="showLinkModal = false">Cancel</b-button>
            <b-button
              type="is-primary"
              icon-left="link"
              :disabled="!selectedFileForLink"
              :loading="linkingFile"
              @click="confirmLinkFile"
            >
              Link This File
            </b-button>
          </footer>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import storageConfigApi from '@/api/storageConfig'
import workflowTemplatesApi from '@/api/workflowTemplates'
import txIngestApi from '@/api/txIngest'
import transcodeJobsApi from '@/api/transcodeJobs'
import episodesApi from '@/api/episodes'
import storageBrowseApi from '@/api/storageBrowse'

export default {
  name: 'BulkEpisodeWorkflow',

  props: {
    mediaId: {
      type: [Number, String],
      required: true
    },
    mediaTitle: {
      type: String,
      default: ''
    },
    seasonNumber: {
      type: [Number, String],
      default: null
    }
  },

  data () {
    return {
      // Storage
      storageConfigs: [],
      outputStorageConfigs: [],
      loadingConfigs: false,
      selectedStorageConfigId: null,
      selectedOutputStorageId: null,

      // Storage Browser (main panel)
      loadingBrowser: false,
      loadingMore: false,
      currentPath: '',
      browserFolders: [],
      browserFiles: [],
      browserSearchTerm: '',
      browserContinuationToken: null,

      // Episodes
      episodes: [],
      selectedRows: [],
      selectAllEpisodes: false,

      // Workflows
      processingWorkflows: [],
      selectedWorkflowType: 'qc',
      selectedWorkflowId: null,

      // Suggestions
      suggesting: false,
      suggestionResults: null,

      // Pattern learning
      showLearnPatternModal: false,
      exampleFilename: '',
      exampleEpisodeNumber: 1,
      learnedPattern: null,
      learnedPatternConfidence: 0,
      learnedPath: '',
      learningPattern: false,

      // Link File Modal
      showLinkModal: false,
      linkingEpisode: null,
      selectedFileForLink: null,
      linkingFile: false,
      modalStorageConfigId: null,
      modalCurrentPath: '',
      modalFolders: [],
      modalFiles: [],
      loadingModalBrowser: false,

      // Actions
      linkingFiles: false,
      submittingJobs: false,
      approvingPlayout: false,
      unlinkingAll: false,
      linkingAllFound: false,

      // Jobs
      recentJobs: []
    }
  },

  computed: {
    selectedEpisodeIds () {
      return this.selectedRows.map(r => r.id)
    },

    episodesWithSuggestions () {
      return this.episodes.filter(ep => ep.suggestion && !ep.txLinked).length
    },

    qcPassedCount () {
      return this.selectedRows.filter(ep => ep.qcStatus === 'passed' && !ep.playoutReady).length
    },

    filteredWorkflows () {
      if (!this.processingWorkflows) return []
      // Filter workflows based on selected type
      const typeMap = {
        qc: ['qc', 'qc_only'],
        transcode: ['transcode', 'format_convert'],
        tx_ingest: ['tx_ingest', 'ingest'],
        playout: ['playout', 'playout_delivery'],
        vod: ['vod', 'vod_delivery']
      }
      const codes = typeMap[this.selectedWorkflowType] || []
      const filtered = this.processingWorkflows.filter(w =>
        codes.some(code => w.template_code?.toLowerCase().includes(code))
      )
      return filtered.length > 0 ? filtered : this.processingWorkflows
    },

    workflowLabel () {
      const labels = {
        qc: 'QC',
        transcode: 'Transcode',
        tx_ingest: 'TX Ingest',
        playout: 'Playout',
        vod: 'VOD'
      }
      return labels[this.selectedWorkflowType] || 'Processing'
    },

    canSubmitJobs () {
      if (this.selectedEpisodeIds.length === 0) return false
      // For TX Ingest, need suggestions or already linked
      if (this.selectedWorkflowType === 'tx_ingest') {
        return this.episodesWithSuggestions > 0 || this.selectedRows.some(ep => ep.txLinked)
      }
      // For other workflows, need TX files linked
      return this.selectedRows.some(ep => ep.txLinked || ep.suggestion)
    },

    // Check if all selected episodes have linked files (input storage already determined)
    allSelectedLinked () {
      if (this.selectedRows.length === 0) return false
      return this.selectedRows.every(ep => ep.txLinked || ep.sourcePath)
    },

    // Count of selected episodes with linked files
    linkedCount () {
      return this.selectedRows.filter(ep => ep.txLinked || ep.sourcePath).length
    },

    // Count of selected episodes that have linked files (for unlink button)
    selectedLinkedCount () {
      return this.selectedRows.filter(ep => ep.txLinked || ep.sourcePath).length
    },

    // Count of episodes with found suggestions (not yet linked)
    foundSuggestionCount () {
      return this.episodes.filter(ep => ep.suggestion && !ep.txLinked).length
    },

    // Storage browser computed properties
    pathParts () {
      if (!this.currentPath) return []
      return this.currentPath.split('/').filter(p => p)
    },

    modalPathParts () {
      if (!this.modalCurrentPath) return []
      return this.modalCurrentPath.split('/').filter(p => p)
    },

    filteredFolders () {
      if (!this.browserSearchTerm) return this.browserFolders
      const term = this.browserSearchTerm.toLowerCase()
      return this.browserFolders.filter(f => f.name.toLowerCase().includes(term))
    },

    filteredFiles () {
      if (!this.browserSearchTerm) return this.browserFiles
      const term = this.browserSearchTerm.toLowerCase()
      return this.browserFiles.filter(f => f.name.toLowerCase().includes(term))
    }
  },

  watch: {
    selectedWorkflowType () {
      // Auto-select first matching workflow
      if (this.filteredWorkflows.length > 0) {
        this.selectedWorkflowId = this.filteredWorkflows[0].id
      }
    },
    selectedStorageConfigId (newVal) {
      // Auto-browse when storage config is selected
      if (newVal) {
        this.browseStorage('')
      }
    },
    modalStorageConfigId (newVal) {
      // Auto-browse modal when storage config changes
      if (newVal && this.showLinkModal) {
        this.browseModalStorage('')
      }
    }
  },

  async mounted () {
    await this.loadAll()
  },

  methods: {
    async loadAll () {
      await Promise.all([
        this.loadStorageConfigs(),
        this.loadProcessingWorkflows(),
        this.loadEpisodes(),
        this.loadRecentJobs()
      ])
    },

    async loadStorageConfigs () {
      this.loadingConfigs = true
      try {
        const configs = await storageConfigApi.getAll()
        this.storageConfigs = configs.filter(c => c.is_active !== false)
        this.outputStorageConfigs = configs.filter(c => c.is_active !== false)
        if (this.storageConfigs.length > 0) {
          this.selectedStorageConfigId = this.storageConfigs[0].id
        }
        if (this.outputStorageConfigs.length > 0) {
          this.selectedOutputStorageId = this.outputStorageConfigs[0].id
        }
      } catch (error) {
        console.error('Failed to load storage configs:', error)
      } finally {
        this.loadingConfigs = false
      }
    },

    async loadProcessingWorkflows () {
      try {
        const response = await workflowTemplatesApi.getAvailable()
        this.processingWorkflows = response.workflows || []
        if (this.filteredWorkflows.length > 0) {
          this.selectedWorkflowId = this.filteredWorkflows[0].id
        }
      } catch (error) {
        console.error('Failed to load workflows:', error)
      }
    },

    async loadEpisodes () {
      try {
        const response = await episodesApi.getAllByMediaId(this.mediaId)
        // Preserve description and episodedata from API so any subsequent
        // episodesApi.update() round-trip (unlinkFile etc.) carries the real
        // values back instead of falling through to "" / {} wipes.
        // Incident: ep 12548 + 12549 (GOEIEMÔRE S03 E57/E58) 2026-06-17/18 —
        // unlinkFile wiped description because this map dropped the field.
        this.episodes = (response || []).map(ep => ({
          id: ep.id,
          episode_number: ep.episode_number,
          title: ep.title,
          description: ep.description,
          episodedata: ep.episodedata,
          txLinked: !!ep.stitched_file_path || !!ep.storage_path,
          sourcePath: ep.stitched_file_path || ep.storage_path || null,
          txFilename: ep.tx_filename || this.extractFilename(ep.stitched_file_path || ep.storage_path),
          qcStatus: ep.stitch_qc_status || null,
          playoutReady: !!ep.playout_approved,
          suggestion: null
        }))
      } catch (error) {
        console.error('Failed to load episodes:', error)
        this.episodes = []
      }
    },

    // Storage Browser Methods
    async browseStorage (path) {
      if (!this.selectedStorageConfigId) {
        this.$buefy.toast.open({ message: 'Please select a storage config first', type: 'is-warning' })
        return
      }
      this.loadingBrowser = true
      this.currentPath = path
      try {
        const result = await storageBrowseApi.browse(this.selectedStorageConfigId, path, '')
        this.browserFolders = (result.folders || []).map(f => ({
          name: f.name || f.prefix?.split('/').filter(p => p).pop() || f.prefix,
          path: f.path || f.prefix
        }))
        this.browserFiles = (result.files || []).map(f => ({
          name: f.name || f.key?.split('/').pop() || f.key,
          path: f.path || f.key,
          size: f.size || f.Size || 0,
          lastModified: f.lastModified || f.LastModified
        }))
        this.browserContinuationToken = result.continuationToken || null
      } catch (error) {
        console.error('Failed to browse storage:', error)
        this.$buefy.toast.open({ message: 'Failed to browse storage', type: 'is-danger' })
      } finally {
        this.loadingBrowser = false
      }
    },

    async loadMoreFiles () {
      if (!this.browserContinuationToken) return
      this.loadingMore = true
      try {
        const result = await storageBrowseApi.browse(
          this.selectedStorageConfigId,
          this.currentPath,
          '',
          this.browserContinuationToken
        )
        // Append to existing files
        const newFiles = (result.files || []).map(f => ({
          name: f.name || f.key?.split('/').pop() || f.key,
          path: f.path || f.key,
          size: f.size || f.Size || 0,
          lastModified: f.lastModified || f.LastModified
        }))
        this.browserFiles = [...this.browserFiles, ...newFiles]
        this.browserContinuationToken = result.continuationToken || null
      } catch (error) {
        console.error('Failed to load more files:', error)
      } finally {
        this.loadingMore = false
      }
    },

    filterBrowserFiles () {
      // Filtering is done via computed properties
    },

    clearBrowserSearch () {
      this.browserSearchTerm = ''
    },

    // Link File Modal Methods
    openLinkModal (episode) {
      this.linkingEpisode = episode
      this.selectedFileForLink = null
      this.modalStorageConfigId = this.selectedStorageConfigId
      this.modalCurrentPath = this.currentPath
      this.modalFolders = [...this.browserFolders]
      this.modalFiles = [...this.browserFiles]
      this.showLinkModal = true
    },

    async browseModalStorage (path) {
      if (!this.modalStorageConfigId) return
      this.loadingModalBrowser = true
      this.modalCurrentPath = path
      try {
        const result = await storageBrowseApi.browse(this.modalStorageConfigId, path, '')
        this.modalFolders = (result.folders || []).map(f => ({
          name: f.name || f.prefix?.split('/').filter(p => p).pop() || f.prefix,
          path: f.path || f.prefix
        }))
        this.modalFiles = (result.files || []).map(f => ({
          name: f.name || f.key?.split('/').pop() || f.key,
          path: f.path || f.key,
          size: f.size || f.Size || 0,
          lastModified: f.lastModified || f.LastModified
        }))
      } catch (error) {
        console.error('Failed to browse modal storage:', error)
      } finally {
        this.loadingModalBrowser = false
      }
    },

    selectFileForLink (file) {
      this.selectedFileForLink = file
    },

    async confirmLinkFile () {
      if (!this.selectedFileForLink || !this.linkingEpisode) return
      this.linkingFile = true
      try {
        // Link the file to the episode
        await txIngestApi.submitBatchTxIngest({
          mediaId: this.mediaId,
          storageConfigId: this.modalStorageConfigId,
          episodes: [{
            episodeId: this.linkingEpisode.id,
            storagePath: this.selectedFileForLink.path
          }],
          linkOnly: true
        })

        // Update local episode data
        const ep = this.episodes.find(e => e.id === this.linkingEpisode.id)
        if (ep) {
          ep.txLinked = true
          ep.sourcePath = this.selectedFileForLink.path
          ep.suggestion = null
        }

        this.$buefy.toast.open({
          message: `Linked "${this.selectedFileForLink.name}" to Episode ${this.linkingEpisode.episode_number}`,
          type: 'is-success'
        })

        this.showLinkModal = false
        this.$emit('files-linked')
      } catch (error) {
        console.error('Failed to link file:', error)
        this.$buefy.toast.open({ message: 'Failed to link file', type: 'is-danger' })
      } finally {
        this.linkingFile = false
      }
    },

    async linkFoundSuggestion (episode) {
      if (!episode.suggestion) return
      this.$set(episode, 'linkingFound', true)
      try {
        await txIngestApi.submitBatchTxIngest({
          mediaId: this.mediaId,
          storageConfigId: this.selectedStorageConfigId,
          episodes: [{
            episodeId: episode.id,
            storagePath: episode.suggestion.path
          }],
          linkOnly: true
        })

        episode.txLinked = true
        episode.sourcePath = episode.suggestion.path
        episode.suggestion = null

        this.$buefy.toast.open({
          message: `Linked file to Episode ${episode.episode_number}`,
          type: 'is-success'
        })
        this.$emit('files-linked')
      } catch (error) {
        console.error('Failed to link suggestion:', error)
        this.$buefy.toast.open({ message: 'Failed to link file', type: 'is-danger' })
      } finally {
        this.$set(episode, 'linkingFound', false)
      }
    },

    dismissSuggestion (episode) {
      episode.suggestion = null
    },

    async linkAllFoundSuggestions () {
      const foundEpisodes = this.episodes.filter(ep => ep.suggestion && !ep.txLinked)
      if (foundEpisodes.length === 0) return

      this.linkingAllFound = true
      try {
        const episodesToLink = foundEpisodes.map(ep => ({
          episodeId: ep.id,
          storagePath: ep.suggestion.path
        }))

        await txIngestApi.submitBatchTxIngest({
          mediaId: this.mediaId,
          storageConfigId: this.selectedStorageConfigId,
          episodes: episodesToLink,
          linkOnly: true
        })

        // Update local state
        foundEpisodes.forEach(ep => {
          ep.txLinked = true
          ep.sourcePath = ep.suggestion.path
          ep.suggestion = null
        })

        this.$buefy.toast.open({
          message: `Linked ${foundEpisodes.length} files`,
          type: 'is-success'
        })
        this.$emit('files-linked')
      } catch (error) {
        console.error('Failed to link all suggestions:', error)
        this.$buefy.toast.open({ message: 'Failed to link files', type: 'is-danger' })
      } finally {
        this.linkingAllFound = false
      }
    },

    async unlinkFile (episode) {
      this.$buefy.dialog.confirm({
        title: 'Unlink File',
        message: `Are you sure you want to unlink the source file from Episode ${episode.episode_number}?`,
        confirmText: 'Unlink',
        type: 'is-warning',
        hasIcon: true,
        onConfirm: async () => {
          try {
            // Pass title/description through as-is — DO NOT fall through to
            // "Episode N" / "" templates, which wipe real metadata. Backend
            // `!== undefined` guard preserves the previous value if these are
            // null/missing.
            await episodesApi.update({
              episodeId: episode.id,
              episodeNumber: episode.episode_number,
              title: episode.title,
              description: episode.description,
              // Use camelCase to match backend API expectations
              storagePath: null,
              stitchedFilePath: null,
              storageConfigId: null
            })
            // Update local state to reflect unlinked status
            episode.txLinked = false
            episode.sourcePath = null
            episode.storage_path = null
            episode.stitched_file_path = null
            episode.storage_config_id = null
            this.$buefy.toast.open({ message: 'File unlinked', type: 'is-success' })
          } catch (error) {
            console.error('Failed to unlink file:', error)
            this.$buefy.toast.open({ message: 'Failed to unlink file', type: 'is-danger' })
          }
        }
      })
    },

    async unlinkSelectedFiles () {
      const linkedEpisodes = this.selectedRows.filter(ep => ep.txLinked || ep.sourcePath)
      if (linkedEpisodes.length === 0) {
        this.$buefy.toast.open({ message: 'No linked files to unlink', type: 'is-warning' })
        return
      }

      this.$buefy.dialog.confirm({
        title: 'Unlink Selected Files',
        message: `Are you sure you want to unlink source files from <strong>${linkedEpisodes.length}</strong> episodes?<br><br>This will allow you to re-run auto-suggest with a new pattern.`,
        confirmText: `Unlink ${linkedEpisodes.length} Files`,
        type: 'is-warning',
        hasIcon: true,
        onConfirm: async () => {
          this.unlinkingAll = true
          try {
            const episodeIds = linkedEpisodes.map(ep => ep.id)
            const result = await episodesApi.bulkUnlink(episodeIds, this.mediaId)

            // Update local state for all unlinked episodes
            for (const ep of linkedEpisodes) {
              ep.txLinked = false
              ep.sourcePath = null
              ep.storage_path = null
              ep.stitched_file_path = null
              ep.storage_config_id = null
              ep.suggestion = null
            }

            // Clear suggestion results since files are now unlinked
            this.suggestionResults = null

            this.$buefy.toast.open({
              message: `Unlinked ${result.unlinkedCount || linkedEpisodes.length} files`,
              type: 'is-success'
            })
          } catch (error) {
            console.error('Failed to unlink files:', error)
            this.$buefy.toast.open({ message: 'Failed to unlink files', type: 'is-danger' })
          } finally {
            this.unlinkingAll = false
          }
        }
      })
    },

    truncatePath (path) {
      if (!path) return ''
      // Show full filename (last segment) without truncation
      // Only truncate the folder path if very deep
      const parts = path.split('/')
      if (parts.length <= 5) return path
      return '.../' + parts.slice(-4).join('/')
    },

    formatFileSize (bytes) {
      if (!bytes || bytes === 0) return '-'
      const units = ['B', 'KB', 'MB', 'GB']
      let size = bytes
      let unitIndex = 0
      while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024
        unitIndex++
      }
      return `${size.toFixed(1)} ${units[unitIndex]}`
    },

    async loadRecentJobs () {
      try {
        const response = await txIngestApi.getVodDeliveryJobs({ mediaId: this.mediaId, limit: 20 })
        this.recentJobs = (response.jobs || []).map(job => ({
          id: job.id,
          episodeNumber: job.episode_number,
          type: job.workflow_type || job.type,
          status: job.status,
          createdAt: job.created_at
        }))
      } catch (error) {
        console.error('Failed to load recent jobs:', error)
      }
    },

    extractFilename (path) {
      if (!path) return null
      return path.split('/').pop()
    },

    toggleSelectAll (value) {
      if (value) {
        this.selectedRows = [...this.episodes]
      } else {
        this.selectedRows = []
      }
    },

    // Pattern Learning
    openLearnPatternModal () {
      this.exampleFilename = ''
      this.exampleEpisodeNumber = 1
      this.showLearnPatternModal = true
    },

    async learnPattern () {
      if (!this.exampleFilename) {
        this.$buefy.toast.open({ message: 'Please enter an example filename', type: 'is-warning' })
        return
      }
      this.learningPattern = true
      try {
        const result = await storageBrowseApi.learnPatternFromExample(
          this.exampleFilename,
          this.mediaTitle || '',
          this.seasonNumber,
          this.exampleEpisodeNumber,
          null // No segment number for single-file mode
        )
        if (result.success) {
          this.learnedPattern = result.pattern
          this.learnedPatternConfidence = result.confidence
        } else {
          this.$buefy.toast.open({ message: 'Could not learn pattern', type: 'is-danger' })
        }
      } catch (error) {
        console.error('Failed to learn pattern:', error)
        this.$buefy.toast.open({ message: 'Failed to learn pattern', type: 'is-danger' })
      } finally {
        this.learningPattern = false
      }
    },

    applyLearnedPattern () {
      this.$buefy.toast.open({ message: `Pattern learned: ${this.learnedPattern}`, type: 'is-success' })
      this.showLearnPatternModal = false
    },

    clearLearnedPattern () {
      this.learnedPattern = null
      this.learnedPatternConfidence = 0
      this.learnedPath = ''
    },

    // Auto-Suggest
    async runAutoSuggest () {
      if (!this.selectedStorageConfigId) {
        this.$buefy.toast.open({ message: 'Please select a storage config', type: 'is-warning' })
        return
      }
      this.suggesting = true
      try {
        const result = await txIngestApi.suggestTxFilesForSeason({
          mediaId: this.mediaId,
          storageConfigId: this.selectedStorageConfigId,
          storagePath: this.learnedPath || '',
          customPattern: this.learnedPattern
        })

        // Merge suggestions into episodes
        // CRITICAL: Backend returns suggestedFile.path and suggestedFile.name
        // Not storagePath/filename at the top level
        const suggestions = result.suggestions || []
        let matchedCount = 0

        // Debug: Log what the backend actually returned
        console.log('[AutoSuggest] Backend response:', {
          totalFiles: result.totalFiles,
          totalEpisodes: result.totalEpisodes,
          matchedEpisodes: result.matchedEpisodes,
          storagePath: result.storagePath
        })
        // Show first 3 suggestions with their suggestedFile status
        console.log('[AutoSuggest] Sample suggestions:', suggestions.slice(0, 3).map(s => ({
          episodeId: s.episodeId,
          episodeNumber: s.episodeNumber,
          matched: s.matched,
          hasSuggestedFile: !!s.suggestedFile,
          fileName: s.suggestedFile?.name || 'none'
        })))
        console.log('[AutoSuggest] Episode IDs in component:', this.episodes.slice(0, 3).map(e => ({ id: e.id, type: typeof e.id })))

        for (const suggestion of suggestions) {
          // Use loose equality to handle string/number mismatch
          const ep = this.episodes.find(e => String(e.id) === String(suggestion.episodeId))
          if (ep && suggestion.suggestedFile) {
            // Only set suggestion if an actual file was found in S3
            // Use $set for Vue reactivity - new property on existing object
            this.$set(ep, 'suggestion', {
              path: suggestion.suggestedFile.path,
              filename: suggestion.suggestedFile.name
            })
            matchedCount++
            console.log(`[AutoSuggest] Matched ep ${ep.episode_number}: ${suggestion.suggestedFile.name}`)
          } else if (suggestion.suggestedFile) {
            console.warn(`[AutoSuggest] Could not find episode with ID ${suggestion.episodeId} (type: ${typeof suggestion.episodeId})`)
          }
        }
        console.log('[AutoSuggest] Total matched:', matchedCount)

        this.suggestionResults = {
          stats: {
            totalEpisodes: this.episodes.length,
            matched: matchedCount,
            notFound: this.episodes.length - matchedCount - this.episodes.filter(e => e.txLinked).length,
            alreadyLinked: this.episodes.filter(e => e.txLinked).length
          }
        }

        // Use matchedCount (files actually matched to episodes) not suggestions.length (all episodes)
        this.$buefy.toast.open({
          message: matchedCount > 0
            ? `Found ${matchedCount} matching TX files (scanned ${result.totalFiles || 0} files)`
            : `No matching files found (scanned ${result.totalFiles || 0} files in ${result.storagePath || 'root'})`,
          type: matchedCount > 0 ? 'is-success' : 'is-warning'
        })
      } catch (error) {
        console.error('Auto-suggest failed:', error)
        this.$buefy.toast.open({ message: 'Auto-suggest failed', type: 'is-danger' })
      } finally {
        this.suggesting = false
      }
    },

    // Link Files
    async linkSuggestedFiles () {
      const toLink = this.episodes.filter(ep => ep.suggestion && !ep.txLinked)
      if (toLink.length === 0) return

      this.linkingFiles = true
      try {
        const result = await txIngestApi.submitBatchTxIngest({
          mediaId: this.mediaId,
          storageConfigId: this.selectedStorageConfigId,
          workflowTemplateId: null, // Just link, don't process
          episodes: toLink.map(ep => ({
            episodeId: ep.id,
            storagePath: ep.suggestion.path
          })),
          linkOnly: true
        })

        // Update local state
        for (const ep of toLink) {
          ep.txLinked = true
          ep.sourcePath = ep.suggestion.path // Store source path, don't overwrite txFilename
        }

        this.$buefy.toast.open({
          message: `Linked ${result.linked || toLink.length} TX files`,
          type: 'is-success'
        })
        this.$emit('files-linked')
      } catch (error) {
        console.error('Failed to link files:', error)
        this.$buefy.toast.open({ message: 'Failed to link files', type: 'is-danger' })
      } finally {
        this.linkingFiles = false
      }
    },

    // Submit Workflow Jobs
    async submitWorkflowJobs () {
      if (this.selectedEpisodeIds.length === 0) return

      this.submittingJobs = true
      try {
        // DEBUG: Log selection state before filtering
        console.log('[BulkEpisodeWorkflow] ===== SUBMIT WORKFLOW JOBS =====')
        console.log('[BulkEpisodeWorkflow] selectedWorkflowType:', this.selectedWorkflowType)
        console.log('[BulkEpisodeWorkflow] selectedWorkflowId:', this.selectedWorkflowId)
        console.log('[BulkEpisodeWorkflow] selectedStorageConfigId:', this.selectedStorageConfigId)
        console.log('[BulkEpisodeWorkflow] selectedOutputStorageId:', this.selectedOutputStorageId)
        console.log('[BulkEpisodeWorkflow] selectedRows:', this.selectedRows.map(ep => ({
          id: ep.id,
          episode_number: ep.episode_number,
          txLinked: ep.txLinked,
          sourcePath: ep.sourcePath,
          suggestion: ep.suggestion?.path || null
        })))

        const episodesToProcess = this.selectedRows.filter(ep => ep.txLinked || ep.suggestion)
        console.log('[BulkEpisodeWorkflow] episodesToProcess after filter:', episodesToProcess.length)

        // CRITICAL: If no episodes pass the filter, show warning
        if (episodesToProcess.length === 0) {
          console.warn('[BulkEpisodeWorkflow] NO episodes passed filter! Check txLinked and suggestion values')
          this.$buefy.toast.open({
            message: 'No episodes with linked files to process',
            type: 'is-warning'
          })
          this.submittingJobs = false
          return
        }

        let result

        // Route to correct API based on workflow type
        if (this.selectedWorkflowType === 'transcode') {
          // Use transcode API - expects 'files' array format
          const filesArray = episodesToProcess.map(ep => ({
            path: ep.sourcePath || ep.suggestion?.path,
            mediaId: this.mediaId,
            episodeId: ep.id
          }))
          console.log('[BulkEpisodeWorkflow] Submitting transcode with filesArray:', JSON.stringify(filesArray, null, 2))

          // Check for missing paths
          const missingPaths = filesArray.filter(f => !f.path)
          if (missingPaths.length > 0) {
            console.error('[BulkEpisodeWorkflow] Some files missing path:', missingPaths)
            this.$buefy.toast.open({
              message: `${missingPaths.length} episodes missing source path`,
              type: 'is-danger'
            })
            this.submittingJobs = false
            return
          }

          console.log('[BulkEpisodeWorkflow] Calling transcodeJobsApi.submitBatchTranscode...')
          result = await transcodeJobsApi.submitBatchTranscode({
            workflowTemplateId: this.selectedWorkflowId,
            inputStorageConfigId: this.selectedStorageConfigId,
            outputStorageConfigId: this.selectedOutputStorageId,
            files: filesArray
          })
          console.log('[BulkEpisodeWorkflow] Transcode API result:', result)
        } else {
          // Use tx-ingest API for QC, TX Ingest, and other workflows
          result = await txIngestApi.submitBatchTxIngest({
            mediaId: this.mediaId,
            storageConfigId: this.selectedStorageConfigId,
            workflowTemplateId: this.selectedWorkflowId,
            outputStorageConfigId: this.selectedOutputStorageId,
            workflowType: this.selectedWorkflowType,
            episodes: episodesToProcess.map(ep => ({
              episodeId: ep.id,
              // IMPORTANT: Only use sourcePath (full S3 key) or suggestion path
              // Do NOT fall back to txFilename as it's just the filename without folder path
              storagePath: ep.sourcePath || ep.suggestion?.path
            }))
          })
        }

        // Handle both array (length) and number responses
        const submitted = result.stats?.submitted || result.submitted
        const submittedCount = Array.isArray(submitted) ? submitted.length : (submitted || episodesToProcess.length)
        this.$buefy.toast.open({
          message: `Submitted ${submittedCount} ${this.workflowLabel} jobs`,
          type: 'is-success'
        })

        await this.loadRecentJobs()
        this.$emit('jobs-submitted')
      } catch (error) {
        console.error('Failed to submit jobs:', error)
        const errorMsg = error.response?.data?.error || error.message

        // Check for specific errors about missing spot config or workflow template
        if (errorMsg.includes('spot processing configuration') || errorMsg.includes('workflow template not found')) {
          this.$buefy.dialog.alert({
            title: 'Processing Not Available',
            message: `<p><strong>${errorMsg}</strong></p>
              <p class="mt-2">This may happen because:</p>
              <ul>
                <li>No spot processing stack is configured for this workflow type</li>
                <li>The selected workflow template is not available</li>
              </ul>
              <p class="mt-2"><strong>Tip:</strong> If you only want to link TX files without processing, use the "Link Found Files" button instead.</p>`,
            type: 'is-warning',
            hasIcon: true,
            icon: 'alert',
            ariaRole: 'alertdialog',
            ariaModal: true
          })
        } else {
          this.$buefy.toast.open({ message: 'Failed to submit jobs: ' + errorMsg, type: 'is-danger' })
        }
      } finally {
        this.submittingJobs = false
      }
    },

    // Approve for Playout
    async approveForPlayout () {
      const toApprove = this.selectedRows.filter(ep => ep.qcStatus === 'passed' && !ep.playoutReady)
      if (toApprove.length === 0) return

      this.approvingPlayout = true
      try {
        // Call API to approve episodes for playout
        for (const ep of toApprove) {
          await episodesApi.update(ep.id, { playout_approved: true })
          ep.playoutReady = true
        }

        this.$buefy.toast.open({
          message: `Approved ${toApprove.length} episodes for playout`,
          type: 'is-success'
        })
        this.$emit('playout-approved')
      } catch (error) {
        console.error('Failed to approve for playout:', error)
        this.$buefy.toast.open({ message: 'Failed to approve for playout', type: 'is-danger' })
      } finally {
        this.approvingPlayout = false
      }
    },

    // Helpers
    getStatusType (status) {
      const map = {
        completed: 'is-success',
        processing: 'is-info',
        queued: 'is-warning',
        failed: 'is-danger',
        cancelled: 'is-dark'
      }
      return map[status] || 'is-light'
    },

    formatTimeAgo (dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const now = new Date()
      const diffMs = now - date
      const diffMins = Math.floor(diffMs / 60000)
      if (diffMins < 1) return 'just now'
      if (diffMins < 60) return `${diffMins}m ago`
      const diffHours = Math.floor(diffMins / 60)
      if (diffHours < 24) return `${diffHours}h ago`
      const diffDays = Math.floor(diffHours / 24)
      return `${diffDays}d ago`
    }
  }
}
</script>

<style scoped>
.bulk-episode-workflow .box {
  margin-bottom: 1rem;
}

.ml-2 {
  margin-left: 0.5rem;
}

.mt-4 {
  margin-top: 1rem;
}

.py-4 {
  padding-top: 1rem;
  padding-bottom: 1rem;
}

/* Storage Browser Styles */
.storage-browser-box {
  background-color: #f9fafb;
}

.browser-content .folder-row {
  cursor: pointer;
}

.browser-content .folder-row:hover {
  background-color: #fff3cd !important;
}

.browser-content .file-row {
  cursor: pointer;
}

.browser-content .file-row:hover {
  background-color: #e3f2fd !important;
}

.browser-content .file-row.is-selected {
  background-color: #c8e6c9 !important;
}

.modal-browser-content .folder-row {
  cursor: pointer;
}

.modal-browser-content .folder-row:hover {
  background-color: #fff3cd !important;
}

.modal-browser-content .file-row {
  cursor: pointer;
}

.modal-browser-content .file-row:hover {
  background-color: #e3f2fd !important;
}

.modal-browser-content .file-row.is-selected {
  background-color: #c8e6c9 !important;
}

.source-path {
  max-width: 600px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  vertical-align: middle;
}

.breadcrumb {
  background: white;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  margin-bottom: 0.5rem;
}

.breadcrumb li + li::before {
  color: #b5b5b5;
}
</style>
